import * as styles from "./Pagination.css";

import { clsx } from "clsx";
import type { JSX } from "react";

import { useControlContext } from "@frontend/contexts";
import { mergeRefs } from "@frontend/utils/mergeRefs";
import { useLingui } from "@lingui/react/macro";

import type { PaginationProps } from "./Pagination.types";
import { PaginationDots } from "./PaginationDots";
import { PaginationNavigationButton } from "./PaginationNavigationButton";
import { PaginationPageJump } from "./PaginationPageJump";
import { PaginationPages } from "./PaginationPages";
import { fitPaginationRange } from "./getPaginationRange";
import { getPaginationSummary } from "./getPaginationSummary";
import type { PaginationSummary } from "./getPaginationSummary";
import { usePaginationFit } from "./usePaginationFit";

const CONTAINER_CLASS_NAMES = {
  default: styles.roundedItems,
  group: styles.group,
  minimal: [styles.roundedItems, styles.minimal],
  dots: styles.dots,
} as const;

export function Pagination({
  page,
  pageCount,
  onPageChange,
  siblingCount,
  boundaryCount,
  size,
  variant = "default",
  radius = "sm",
  align = "start",
  totalItems,
  pageSize,
  summary,
  jump = false,
  disabled,
  onPagePrefetch,
  className,
  ref,
  ...props
}: PaginationProps): JSX.Element {
  const { t } = useLingui();

  const inherited = useControlContext();
  const resolvedSize = size ?? inherited.size;

  const { navRef, listRef, asideRef, metrics, compact } = usePaginationFit({
    hasNavigationButtons: variant !== "dots",
    resetKey: `${variant}:${resolvedSize}:${String(pageCount).length}`,
  });

  const range = fitPaginationRange({
    page,
    pageCount,
    siblingCount,
    boundaryCount,
    metrics: variant === "minimal" ? null : metrics,
  });

  const counts =
    totalItems === undefined
      ? null
      : getPaginationSummary({ page, pageCount, totalItems, pageSize });

  // Destructured so the catalog gets named placeholders rather than {0} {1} {2}
  const describe = ({ from, to, total }: PaginationSummary) =>
    t`Showing ${from} to ${to} of ${total} results`;

  const summaryContent = summary ?? (counts && describe(counts));

  const hasAside = Boolean(summaryContent) || jump;

  return (
    <nav
      data-slot="pagination"
      data-variant={variant}
      aria-label="Pagination"
      className={clsx(
        styles.pagination,
        hasAside ? styles.withAside : styles.alignVariants[align],
        className,
      )}
      {...props}
      ref={mergeRefs(navRef, ref)}
    >
      {hasAside && (
        <div
          data-slot="pagination-aside"
          ref={asideRef}
          className={styles.aside}
        >
          {summaryContent && (
            <span
              data-slot="pagination-summary"
              className={styles.summary({ size: resolvedSize })}
              aria-live="polite"
            >
              {summaryContent}
            </span>
          )}

          {jump && (
            <PaginationPageJump
              page={page}
              pageCount={pageCount}
              size={resolvedSize}
              disabled={disabled}
              onPageChange={onPageChange}
            />
          )}
        </div>
      )}

      <div
        data-slot="pagination-list"
        ref={listRef}
        className={clsx(
          styles.list,
          styles.radiusVariants[radius],
          CONTAINER_CLASS_NAMES[variant],
        )}
      >
        {variant === "dots" ? (
          <PaginationDots
            range={range}
            page={page}
            size={resolvedSize}
            disabled={disabled}
            onPageChange={onPageChange}
          />
        ) : (
          <>
            <PaginationNavigationButton
              direction="previous"
              variant={variant}
              size={resolvedSize}
              compact={compact}
              disabled={disabled || page <= 1}
              onClick={() => onPageChange(page - 1)}
              onPrefetch={onPagePrefetch && (() => onPagePrefetch(page - 1))}
            />

            {variant === "minimal" ? (
              <span
                data-slot="pagination-status"
                className={styles.status({ size: resolvedSize })}
                aria-live="polite"
              >
                Page {page} sur {pageCount}
              </span>
            ) : (
              <PaginationPages
                range={range}
                page={page}
                size={resolvedSize}
                disabled={disabled}
                onPageChange={onPageChange}
                onPagePrefetch={onPagePrefetch}
              />
            )}

            <PaginationNavigationButton
              direction="next"
              variant={variant}
              size={resolvedSize}
              compact={compact}
              disabled={disabled || page >= pageCount}
              onClick={() => onPageChange(page + 1)}
              onPrefetch={onPagePrefetch && (() => onPagePrefetch(page + 1))}
            />
          </>
        )}
      </div>
    </nav>
  );
}
